// NEW_FILE_JUSTIFIED: Lead-scoped Gmail history panel for the lead detail page. Distinct from
// EmailThread/EmailList (src/components/messages/) which are the shared-inbox reader wired up
// with reply/forward/AI-reply actions — inappropriate for a read-only summary embedded in the
// lead card. EmailMessageCard inside EmailThread.tsx is not exported and expects a different
// (UI-transformed) message shape, so this renders the flat gmailMessages rows directly instead.
import { Component, type ReactNode } from 'react';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the official client-side React bindings package, not a server module
import { format } from 'date-fns';
import { Mail, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { api } from '@convex/_generated/api';
import type { ILeadEmailHistoryPanelProps, TLeadEmailMessage } from '@/@types/leadPanels';

/**
 * CLASS_COMPONENT_JUSTIFIED: error boundaries require a class component — no hook equivalent
 * exists in stable React. getMessagesForLead throws ConvexError('Not authorized') when the
 * caller can't access the lead, and this app has no ErrorBoundary anywhere in its tree, so an
 * uncaught throw here would white-screen the whole lead detail page. This contains the failure
 * to just this panel instead of hiding it — see the rendered message below.
 */
class EmailHistoryBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card>
          <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <span role="alert" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              Unable to load email history for this lead.
            </span>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

function EmailHistoryContent({ leadId }: ILeadEmailHistoryPanelProps) {
  const messages = useQuery(api.gmail.queries.getMessagesForLead, { leadId });

  if (messages === undefined) {
    return (
      <Card aria-busy="true">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
            Email history
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <span className="sr-only" role="status">
            Loading email history
          </span>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-5/6" />
        </CardContent>
      </Card>
    );
  }

  if (messages.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
            Email history
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            title="No emails linked yet"
            description="Emails linked to this lead will appear here."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
          Email history
          <Badge variant="secondary">{messages.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {messages.map((message: TLeadEmailMessage) => {
          const isOutgoing = message.direction === 'outgoing';
          const bodyPreview = message.bodyText ?? message.snippet;

          return (
            <Collapsible key={message._id} className="rounded-lg border border-border">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-auto w-full justify-between gap-3 rounded-lg px-3 py-3 text-left font-normal"
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <Badge variant={isOutgoing ? 'default' : 'secondary'} className="shrink-0">
                      {isOutgoing ? 'Sent' : 'Received'}
                    </Badge>
                    <span className="truncate text-sm font-medium text-foreground">
                      {message.subject || '(no subject)'}
                    </span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                    {format(message.internalDate, 'MMM d, yyyy · h:mm a')}
                  </span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-3 pb-3">
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {bodyPreview || '(no content)'}
                </p>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </CardContent>
    </Card>
  );
}

export function LeadEmailHistoryPanel({ leadId }: ILeadEmailHistoryPanelProps) {
  return (
    <EmailHistoryBoundary>
      <EmailHistoryContent leadId={leadId} />
    </EmailHistoryBoundary>
  );
}
